import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCurrentEditor } from '@tiptap/react';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import MenuList from '@components/MenuList';
import { ModalConfirm } from '@components/Modal';
import { useTranslation } from '@hooks';
import { addTipTapMarkTag, parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { inboxTemplatesService } from '@services';
import { alertActions, inboxTemplatesActions } from '@actions';
import { ROLE } from '@constants/user';
import { InboxTemplatesIcon } from 'assets/images/icons';
import CreateTemplateModal from './CreateTemplateModal';
import { StyledSaveIcon, StyledDeleteIcon } from './styles';

const InboxTemplateMenu = ({ disabled, content, setContent, tags }) => {
    const { t } = useTranslation();
    const { editor } = useCurrentEditor();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const [anchorElMain, setAnchorElMain] = useState(null);
    const [anchorElDelete, setAnchorElDelete] = useState(null);
    const [anchorElEdit, setAnchorElEdit] = useState(null);

    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const { data } = useSelector(state => state.inboxTemplates);
    const companies = useSelector(state => state.account?.account?.companies || []);
    const user = useSelector(state => state.auth?.user);

    useEffect(() => {
        dispatch(inboxTemplatesActions.getAll());
    }, []);

    const handleOpenMain = event => {
        if (isMobile) {
            setMobileMenuOpen(true);
        } else {
            setAnchorElMain(event.currentTarget);
            setAnchorElDelete(null);
            setAnchorElEdit(null);
        }
    };
    const handleOpenDelete = event => {
        setAnchorElDelete(event.currentTarget);
        setAnchorElEdit(null);
    };
    const handleOpenEdit = event => {
        setAnchorElEdit(event.currentTarget);
        setAnchorElDelete(null);
    };

    const handleCloseMain = () => setAnchorElMain(null);

    const onClickTemplate = template => {
        const locationName =
            tags?.locations?.length > 1 ? '' : companies.find(c => c.companyId === tags?.locations[0])?.locationName;
        const newMessage = template.message
            ?.replace(/\[name\]/g, tags?.firstName || '')
            ?.replace(/\[last_name\]/g, tags?.lastName || '')
            ?.replace(/\[location\]/g, locationName);
        editor?.commands?.setContent(addTipTapMarkTag(newMessage));
        setContent(JSON.stringify(editor?.getJSON() || {}));
        setMobileMenuOpen(false);
        setAnchorElMain(null);
    };

    const onEditTemplate = async () => {
        try {
            await inboxTemplatesService.addOrUpdateInboxTemplate({
                ...selectedTemplate,
                message: parseTipTapJSONToPlainText(content),
            });
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            await dispatch(inboxTemplatesActions.getAll());
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.editFail')));
        }
        setEditModalOpen(false);
        setAnchorElEdit(null);
        setAnchorElMain(null);
        setSelectedTemplate(null);
    };

    const onDeleteTemplate = async () => {
        try {
            await inboxTemplatesService.deleteInboxTemplate(selectedTemplate.id);
            dispatch(alertActions.success(t('alertMessages.deleteSuccess')));
            await dispatch(inboxTemplatesActions.getAll());
            setDeleteModalOpen(false);
            setAnchorElDelete(null);
            setAnchorElMain(null);
            setSelectedTemplate(null);
        } catch (error) {
            dispatch(alertActions.error(t('alertMessages.deleteFail')));
        }
    };

    const ACTIONS = {
        INSERT: template => () => onClickTemplate(template),
        EDIT: template => () => {
            setSelectedTemplate(template);
            setEditModalOpen(true);
        },
        DELETE: template => () => {
            setSelectedTemplate(template);
            setDeleteModalOpen(true);
        },
    };

    function mapTemplates(data, action) {
        return data
            .filter(t => {
                if (action === 'INSERT') return true;
                return (user.role === ROLE.USER && !t.shared) || user.role !== ROLE.USER;
            })
            .map(template => ({
                icon: null,
                text: template.name,
                sub: {},
                onClick: ACTIONS[action](template),
            }));
    }

    const MAIN_MENU = [
        ...mapTemplates(data, 'INSERT'),
        ...[
            {
                divider: true,
                icon: <StyledSaveIcon />,
                text: t('RespondTemplate.saveReplyAsTemplate'),
                sub: {
                    header: t('RespondTemplate.overwriteTemplate'),
                    list: [
                        ...mapTemplates(data, 'EDIT'),
                        {
                            divider: true,
                            icon: <StyledSaveIcon />,
                            text: t('RespondTemplate.saveAsNewTemplate'),
                            sub: {},
                            onClick: () => setCreateModalOpen(true),
                        },
                    ],
                },
                anchorEl: anchorElEdit,
                onClick: handleOpenEdit,
            },
            {
                icon: <StyledDeleteIcon />,
                text: t('RespondTemplate.deleteTemplate'),
                sub: {
                    header: t('RespondTemplate.deleteTemplate'),
                    list: mapTemplates(data, 'DELETE'),
                },
                anchorEl: anchorElDelete,
                onClick: handleOpenDelete,
            },
        ],
    ];

    return (
        <>
            <MenuList
                options={MAIN_MENU}
                anchorEl={anchorElMain}
                onCloseMenu={handleCloseMain}
                openModal={isMobileMenuOpen}
                handleCloseModal={() => setMobileMenuOpen(false)}
                mainTitle={t('RespondTemplate.insertTemplate')}
                menuButton={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={handleOpenMain}
                        disabled={disabled}
                        sx={{ height: 18, width: 18, padding: '2px !important' }}
                    >
                        <InboxTemplatesIcon />
                    </IconButton>
                }
            />
            {isDeleteModalOpen ? (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDeleteTemplate}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                />
            ) : null}
            {isEditModalOpen ? (
                <ModalConfirm
                    isOpen={isEditModalOpen}
                    handleClose={() => setEditModalOpen(false)}
                    title={t('RespondTemplate.editTemplate')}
                    description={t('RespondTemplate.editTemplateDescription')}
                    onPrimaryAction={onEditTemplate}
                    primaryActionText={t('buttons.save')}
                />
            ) : null}
            {isCreateModalOpen ? (
                <CreateTemplateModal
                    user={user}
                    content={content}
                    isCreateModalOpen={isCreateModalOpen}
                    setCreateModalOpen={setCreateModalOpen}
                    setAnchorElEdit={setAnchorElEdit}
                />
            ) : null}
        </>
    );
};

export default InboxTemplateMenu;
